// Utilitários para validações de formulários

/**
 * Valida CNPJ brasileiro
 * @param {string} cnpj - CNPJ a ser validado
 * @returns {boolean} - true se válido, false caso contrário
 */
export function validateCNPJ(cnpj) {
  if (!cnpj) return false;
  
  // Remove caracteres não numéricos
  const cleanCNPJ = cnpj.replace(/\D/g, '');
  
  // Verifica se tem 14 dígitos
  if (cleanCNPJ.length !== 14) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{13}$/.test(cleanCNPJ)) return false;
  
  // Validação do primeiro dígito verificador
  let sum = 0;
  let weight = 5;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  let digit = 11 - (sum % 11);
  if (digit > 9) digit = 0;
  if (parseInt(cleanCNPJ.charAt(12)) !== digit) return false;
  
  // Validação do segundo dígito verificador
  sum = 0;
  weight = 6;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  digit = 11 - (sum % 11);
  if (digit > 9) digit = 0;
  if (parseInt(cleanCNPJ.charAt(13)) !== digit) return false;
  
  return true;
}

/**
 * Formata CNPJ para exibição
 * @param {string} cnpj - CNPJ a ser formatado
 * @returns {string} - CNPJ formatado
 */
export function formatCNPJ(cnpj) {
  if (!cnpj) return '';
  const cleanCNPJ = cnpj.replace(/\D/g, '');
  if (cleanCNPJ.length !== 14) return cnpj;
  return cleanCNPJ.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

/**
 * Aplica máscara de CNPJ durante digitação
 * @param {string} value - Valor digitado
 * @returns {string} - Valor com máscara aplicada
 */
export function applyCNPJMask(value) {
  if (!value) return '';
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 2) return cleanValue;
  if (cleanValue.length <= 5) return `${cleanValue.slice(0, 2)}.${cleanValue.slice(2)}`;
  if (cleanValue.length <= 8) return `${cleanValue.slice(0, 2)}.${cleanValue.slice(2, 5)}.${cleanValue.slice(5)}`;
  if (cleanValue.length <= 12) return `${cleanValue.slice(0, 2)}.${cleanValue.slice(2, 5)}.${cleanValue.slice(5, 8)}/${cleanValue.slice(8)}`;
  return `${cleanValue.slice(0, 2)}.${cleanValue.slice(2, 5)}.${cleanValue.slice(5, 8)}/${cleanValue.slice(8, 12)}-${cleanValue.slice(12, 14)}`;
}

/**
 * Valida telefone brasileiro
 * @param {string} phone - Telefone a ser validado
 * @returns {boolean} - true se válido, false caso contrário
 */
export function validatePhone(phone) {
  if (!phone) return false;
  
  // Remove caracteres não numéricos
  const cleanPhone = phone.replace(/\D/g, '');
  
  // Verifica se tem 10 dígitos (fixo) ou 11 dígitos (celular com 9 dígitos)
  if (cleanPhone.length !== 10 && cleanPhone.length !== 11) return false;
  
  // Verifica se tem DDD válido (não começa com 0, exceto para números especiais)
  const ddd = parseInt(cleanPhone.substring(0, 2));
  if (ddd < 11 || ddd > 99) return false;
  
  return true;
}

/**
 * Formata telefone para exibição
 * @param {string} phone - Telefone a ser formatado
 * @returns {string} - Telefone formatado
 */
export function formatPhone(phone) {
  if (!phone) return '';
  const cleanPhone = phone.replace(/\D/g, '');
  
  if (cleanPhone.length === 10) {
    // Formato fixo: (XX) 9999-9999
    return cleanPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  } else if (cleanPhone.length === 11) {
    // Formato celular: (XX) 99999-9999
    return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  
  return phone;
}

/**
 * Aplica máscara de telefone durante digitação
 * @param {string} value - Valor digitado
 * @returns {string} - Valor com máscara aplicada
 */
export function applyPhoneMask(value) {
  if (!value) return '';
  const cleanValue = value.replace(/\D/g, '');
  
  if (cleanValue.length <= 2) return cleanValue;
  if (cleanValue.length <= 6) return `(${cleanValue.slice(0, 2)}) ${cleanValue.slice(2)}`;
  if (cleanValue.length <= 10) {
    // Formato fixo: (XX) 9999-9999
    return `(${cleanValue.slice(0, 2)}) ${cleanValue.slice(2, 6)}-${cleanValue.slice(6)}`;
  }
  if (cleanValue.length <= 11) {
    // Formato celular: (XX) 99999-9999
    return `(${cleanValue.slice(0, 2)}) ${cleanValue.slice(2, 7)}-${cleanValue.slice(7)}`;
  }
  
  return `(${cleanValue.slice(0, 2)}) ${cleanValue.slice(2, 7)}-${cleanValue.slice(7, 11)}`;
}

/**
 * Valida CEP brasileiro
 * @param {string} cep - CEP a ser validado
 * @returns {boolean} - true se válido, false caso contrário
 */
export function validateCEP(cep) {
  if (!cep) return false;
  
  // Remove caracteres não numéricos
  const cleanCEP = cep.replace(/\D/g, '');
  
  // Verifica se tem 8 dígitos
  return cleanCEP.length === 8;
}

/**
 * Formata CEP para exibição
 * @param {string} cep - CEP a ser formatado
 * @returns {string} - CEP formatado
 */
export function formatCEP(cep) {
  if (!cep) return '';
  const cleanCEP = cep.replace(/\D/g, '');
  if (cleanCEP.length !== 8) return cep;
  return cleanCEP.replace(/(\d{5})(\d{3})/, '$1-$2');
}

/**
 * Aplica máscara de CEP durante digitação
 * @param {string} value - Valor digitado
 * @returns {string} - Valor com máscara aplicada
 */
export function applyCEPMask(value) {
  if (!value) return '';
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 5) return cleanValue;
  return `${cleanValue.slice(0, 5)}-${cleanValue.slice(5, 8)}`;
}

/**
 * Busca endereço pelo CEP usando API ViaCEP
 * @param {string} cep - CEP a ser consultado
 * @returns {Promise<Object>} - Dados do endereço
 */
export async function fetchAddressByCEP(cep) {
  if (!validateCEP(cep)) {
    throw new Error('CEP inválido');
  }
  
  const cleanCEP = cep.replace(/\D/g, '');
  const url = `https://viacep.com.br/ws/${cleanCEP}/json/`;
  
  try {
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.erro) {
      throw new Error('CEP não encontrado');
    }
    
    return {
      cep: data.cep,
      logradouro: data.logradouro,
      complemento: data.complemento,
      bairro: data.bairro,
      localidade: data.localidade,
      uf: data.uf,
      ibge: data.ibge,
      gia: data.gia,
      ddd: data.ddd,
      siafi: data.siafi
    };
  } catch (error) {
    throw new Error('Erro ao consultar CEP: ' + error.message);
  }
}

/**
 * Valida e-mail
 * @param {string} email - E-mail a ser validado
 * @returns {boolean} - true se válido, false caso contrário
 */
export function validateEmail(email) {
  if (!email) return false;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Valida registro ANS
 * @param {string} ansRegistry - Registro ANS a ser validado
 * @returns {boolean} - true se válido, false caso contrário
 */
export function validateANSRegistry(ansRegistry) {
  if (!ansRegistry) return false;
  
  // Remove caracteres não numéricos
  const cleanANS = ansRegistry.replace(/\D/g, '');
  
  // Verifica se tem entre 6 e 10 dígitos
  return cleanANS.length >= 6 && cleanANS.length <= 10;
}

/**
 * Valida CPF brasileiro
 * @param {string} cpf - CPF a ser validado
 * @returns {boolean} - true se válido, false caso contrário
 */
export function validateCPF(cpf) {
  if (!cpf) return false;
  
  // Remove caracteres não numéricos
  const cleanCPF = cpf.replace(/\D/g, '');
  
  // Verifica se tem 11 dígitos
  if (cleanCPF.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(cleanCPF)) return false;
  
  // Validação do primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  }
  let digit = 11 - (sum % 11);
  if (digit > 9) digit = 0;
  if (parseInt(cleanCPF.charAt(9)) !== digit) return false;
  
  // Validação do segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  }
  digit = 11 - (sum % 11);
  if (digit > 9) digit = 0;
  if (parseInt(cleanCPF.charAt(10)) !== digit) return false;
  
  return true;
}

/**
 * Formata CPF para exibição
 * @param {string} cpf - CPF a ser formatado
 * @returns {string} - CPF formatado
 */
export function formatCPF(cpf) {
  if (!cpf) return '';
  const cleanCPF = cpf.replace(/\D/g, '');
  if (cleanCPF.length !== 11) return cpf;
  return cleanCPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Aplica máscara de CPF durante digitação
 * @param {string} value - Valor digitado
 * @returns {string} - Valor com máscara aplicada
 */
export function applyCPFMask(value) {
  if (!value) return '';
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 3) return cleanValue;
  if (cleanValue.length <= 6) return `${cleanValue.slice(0, 3)}.${cleanValue.slice(3)}`;
  if (cleanValue.length <= 9) return `${cleanValue.slice(0, 3)}.${cleanValue.slice(3, 6)}.${cleanValue.slice(6)}`;
  return `${cleanValue.slice(0, 3)}.${cleanValue.slice(3, 6)}.${cleanValue.slice(6, 9)}-${cleanValue.slice(9, 11)}`;
}

/**
 * Valida CRM (Conselho Regional de Medicina)
 * @param {string} crm - CRM a ser validado
 * @returns {boolean} - true se válido, false caso contrário
 */
export function validateCRM(crm) {
  if (!crm) return false;
  
  // Remove caracteres não numéricos
  const cleanCRM = crm.replace(/\D/g, '');
  
  // Verifica se tem entre 5 e 10 dígitos (varia por estado)
  // Alguns estados têm CRMs com 5-6 dígitos, outros com 8-10 dígitos
  if (cleanCRM.length < 5 || cleanCRM.length > 10) return false;
  
  // Verifica se não são todos os dígitos iguais
  if (/^(\d)\1+$/.test(cleanCRM)) return false;
  
  return true;
}

/**
 * Formata CRM para exibição
 * @param {string} crm - CRM a ser formatado
 * @returns {string} - CRM formatado
 */
export function formatCRM(crm) {
  if (!crm) return '';
  const cleanCRM = crm.replace(/\D/g, '');
  return cleanCRM;
}

/**
 * Aplica máscara de CRM durante digitação
 * @param {string} value - Valor digitado
 * @returns {string} - Valor com máscara aplicada
 */
export function applyCRMMask(value) {
  if (!value) return '';
  const cleanValue = value.replace(/\D/g, '');
  return cleanValue;
}

/**
 * Valida data de nascimento
 * @param {string} birthDate - Data de nascimento no formato YYYY-MM-DD
 * @returns {boolean} - true se válido, false caso contrário
 */
export function validateBirthDate(birthDate) {
  if (!birthDate) return true; // Campo opcional
  
  const date = new Date(birthDate);
  const today = new Date();
  
  // Verifica se é uma data válida
  if (isNaN(date.getTime())) return false;
  
  // Verifica se não é uma data futura
  if (date > today) return false;
  
  // Verifica se não é muito antiga (mais de 120 anos)
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - 120);
  if (date < minDate) return false;
  
  return true;
}

/**
 * Valida idade mínima
 * @param {string} birthDate - Data de nascimento no formato YYYY-MM-DD
 * @param {number} minAge - Idade mínima em anos
 * @returns {boolean} - true se válido, false caso contrário
 */
export function validateMinAge(birthDate, minAge = 18) {
  if (!birthDate) return true; // Campo opcional
  
  const birth = new Date(birthDate);
  const today = new Date();
  
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age >= minAge;
}

/**
 * Formata registro ANS para exibição
 * @param {string} ansRegistry - Registro ANS a ser formatado
 * @returns {string} - Registro ANS formatado
 */
export function formatANSRegistry(ansRegistry) {
  if (!ansRegistry) return '';
  const cleanANS = ansRegistry.replace(/\D/g, '');
  return cleanANS;
}

/**
 * Valida se um campo obrigatório está preenchido
 * @param {string} value - Valor a ser validado
 * @returns {boolean} - true se preenchido, false caso contrário
 */
export function validateRequired(value) {
  return value && value.toString().trim() !== '';
}

/**
 * Valida se um número está dentro de um intervalo
 * @param {number} value - Valor a ser validado
 * @param {number} min - Valor mínimo
 * @param {number} max - Valor máximo
 * @returns {boolean} - true se válido, false caso contrário
 */
export function validateNumberRange(value, min, max) {
  const num = parseFloat(value);
  return !isNaN(num) && num >= min && num <= max;
}

/**
 * Valida se um valor decimal está dentro de um intervalo
 * @param {number} value - Valor a ser validado
 * @param {number} min - Valor mínimo
 * @param {number} max - Valor máximo
 * @param {number} decimalPlaces - Número de casas decimais
 * @returns {boolean} - true se válido, false caso contrário
 */
export function validateDecimalRange(value, min, max, decimalPlaces = 2) {
  const num = parseFloat(value);
  if (isNaN(num)) return false;
  
  // Verifica se o número de casas decimais está correto
  const decimalStr = value.toString().split('.')[1];
  if (decimalStr && decimalStr.length > decimalPlaces) return false;
  
  return num >= min && num <= max;
}

/**
 * Retorna mensagem de erro para um campo específico
 * @param {string} fieldName - Nome do campo
 * @param {string} validationType - Tipo de validação
 * @returns {string} - Mensagem de erro
 */
export function getErrorMessage(fieldName, validationType) {
  const messages = {
    required: `${fieldName} é obrigatório`,
    invalidCNPJ: 'CNPJ inválido',
    invalidCPF: 'CPF inválido',
    invalidCRM: 'CRM inválido',
    invalidPhone: 'Telefone inválido',
    invalidCEP: 'CEP inválido',
    invalidEmail: 'E-mail inválido',
    invalidANS: 'Registro ANS inválido',
    invalidBirthDate: 'Data de nascimento inválida',
    invalidMinAge: 'Idade mínima não atingida',
    invalidNumber: 'Número inválido',
    invalidDecimal: 'Valor decimal inválido',
    cepNotFound: 'CEP não encontrado',
    cepError: 'Erro ao consultar CEP'
  };
  
  return messages[validationType] || 'Campo inválido';
}

/**
 * Valida todos os campos de um formulário de convênio
 * @param {Object} insurance - Dados do convênio
 * @returns {Object} - Objeto com erros encontrados
 */
export function validateInsuranceForm(insurance) {
  const errors = {};
  
  // Validações obrigatórias
  if (!validateRequired(insurance.name)) {
    errors.name = getErrorMessage('Nome', 'required');
  }
  
  if (!validateCNPJ(insurance.cnpj)) {
    errors.cnpj = getErrorMessage('CNPJ', 'invalidCNPJ');
  }
  
  if (!validateANSRegistry(insurance.ans_registry)) {
    errors.ans_registry = getErrorMessage('Registro ANS', 'invalidANS');
  }
  
  if (!validateRequired(insurance.insurance_type)) {
    errors.insurance_type = getErrorMessage('Tipo de Convênio', 'required');
  }
  
  // Validações de localização
  if (!validateCEP(insurance.zip_code)) {
    errors.zip_code = getErrorMessage('CEP', 'invalidCEP');
  }
  
  if (!validateRequired(insurance.state)) {
    errors.state = getErrorMessage('UF', 'required');
  }
  
  if (!validateRequired(insurance.city)) {
    errors.city = getErrorMessage('Cidade', 'required');
  }
  
  if (!validateRequired(insurance.street_type)) {
    errors.street_type = getErrorMessage('Tipo de Logradouro', 'required');
  }
  
  if (!validateRequired(insurance.street)) {
    errors.street = getErrorMessage('Logradouro', 'required');
  }
  
  if (!validateRequired(insurance.number)) {
    errors.number = getErrorMessage('Número', 'required');
  }
  
  // Validações de contato
  if (!validateEmail(insurance.email1)) {
    errors.email1 = getErrorMessage('E-mail 1', 'invalidEmail');
  }
  
  if (insurance.email2 && !validateEmail(insurance.email2)) {
    errors.email2 = getErrorMessage('E-mail 2', 'invalidEmail');
  }
  
  if (!validateRequired(insurance.contact_name)) {
    errors.contact_name = getErrorMessage('Nome do Contato', 'required');
  }
  
  if (!validatePhone(insurance.phone1)) {
    errors.phone1 = getErrorMessage('Telefone 1', 'invalidPhone');
  }
  
  if (insurance.phone2 && !validatePhone(insurance.phone2)) {
    errors.phone2 = getErrorMessage('Telefone 2', 'invalidPhone');
  }
  
  if (insurance.phone3 && !validatePhone(insurance.phone3)) {
    errors.phone3 = getErrorMessage('Telefone 3', 'invalidPhone');
  }
  
  // Validações de valores numéricos
  if (insurance.square_meter_value !== null && insurance.square_meter_value !== '') {
    if (!validateDecimalRange(insurance.square_meter_value, 0, 999999.99, 2)) {
      errors.square_meter_value = getErrorMessage('Valor do M²', 'invalidDecimal');
    }
  }
  
  if (insurance.delivery_date !== null && insurance.delivery_date !== '') {
    if (!validateNumberRange(insurance.delivery_date, 1, 31)) {
      errors.delivery_date = getErrorMessage('Data de Entrega', 'invalidNumber');
    }
  }
  
  if (insurance.due_day !== null && insurance.due_day !== '') {
    if (!validateNumberRange(insurance.due_day, 1, 31)) {
      errors.due_day = getErrorMessage('Dia do Vencimento', 'invalidNumber');
    }
  }
  
  // Validações de taxas (NFSe)
  const taxFields = ['ir_rate', 'csll_rate', 'cofins_rate', 'inss_rate', 'pis_rate'];
  taxFields.forEach(field => {
    if (insurance[field] !== null && insurance[field] !== '') {
      if (!validateDecimalRange(insurance[field], 0, 100, 2)) {
        errors[field] = getErrorMessage(field.replace('_', ' ').toUpperCase(), 'invalidDecimal');
      }
    }
  });
  
  return errors;
}

/**
 * Valida CNS (Cartão Nacional de Saúde)
 * @param {string} cns - CNS a ser validado
 * @returns {boolean} - true se válido, false caso contrário
 */
export function validateCNS(cns) {
  if (!cns) return false;
  
  // Remove caracteres não numéricos
  const cleanCNS = cns.replace(/\D/g, '');
  
  // Verifica se tem 15 dígitos
  if (cleanCNS.length !== 15) return false;
  
  // Verifica se são todos os dígitos iguais
  if (/^(\d)\1{14}$/.test(cleanCNS)) return false;
  
  // Converte para array de números
  const digits = cleanCNS.split('').map(Number);
  
  // CNS começando com 1, 2, 7, 8 ou 9 são CNS provisórios
  const firstDigit = digits[0];
  if ([1, 2, 7, 8, 9].includes(firstDigit)) {
    // Para CNS provisório, apenas verifica se não é todos zeros
    return !cleanCNS.split('').every(d => d === '0');
  }
  
  // CNS definitivo - validação com dígito verificador
  let soma = 0;
  for (let i = 0; i < 11; i++) {
    soma += digits[i] * (15 - i);
  }
  
  const resto = soma % 11;
  let dv1;
  
  if (resto === 0) {
    dv1 = 0;
  } else if (resto === 1) {
    dv1 = 0;
  } else {
    dv1 = 11 - resto;
  }
  
  // Calcula o segundo dígito
  soma = dv1 * 15;
  for (let i = 1; i < 11; i++) {
    soma += digits[i] * (15 - i + 1);
  }
  
  const resto2 = soma % 11;
  let dv2;
  
  if (resto2 === 0) {
    dv2 = 0;
  } else if (resto2 === 1) {
    dv2 = 0;
  } else {
    dv2 = 11 - resto2;
  }
  
  // Calcula o terceiro dígito
  soma = dv2 * 15;
  for (let i = 2; i < 11; i++) {
    soma += digits[i] * (15 - i + 2);
  }
  
  const resto3 = soma % 11;
  let dv3;
  
  if (resto3 === 0) {
    dv3 = 0;
  } else if (resto3 === 1) {
    dv3 = 0;
  } else {
    dv3 = 11 - resto3;
  }
  
  // Verifica os 3 dígitos verificadores
  return digits[11] === dv1 && digits[12] === dv2 && digits[13] === dv3;
}

/**
 * Formata CNS para exibição
 * @param {string} cns - CNS a ser formatado
 * @returns {string} - CNS formatado
 */
export function formatCNS(cns) {
  if (!cns) return '';
  const cleanCNS = cns.replace(/\D/g, '');
  if (cleanCNS.length !== 15) return cns;
  return cleanCNS.replace(/(\d{3})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4');
}

/**
 * Aplica máscara de CNS em tempo real
 * @param {string} cns - CNS a ser mascarado
 * @returns {string} - CNS com máscara aplicada
 */
export function applyCNSMask(cns) {
  if (!cns) return '';
  const cleanCNS = cns.replace(/\D/g, '');
  if (cleanCNS.length <= 3) return cleanCNS;
  if (cleanCNS.length <= 7) return cleanCNS.replace(/(\d{3})(\d{0,4})/, '$1 $2');
  if (cleanCNS.length <= 11) return cleanCNS.replace(/(\d{3})(\d{4})(\d{0,4})/, '$1 $2 $3');
  return cleanCNS.replace(/(\d{3})(\d{4})(\d{4})(\d{0,4})/, '$1 $2 $3 $4');
}

/**
 * Valida todos os campos de um formulário de médico
 * @param {Object} doctor - Dados do médico
 * @returns {Object} - Objeto com erros encontrados
 */
export function validateDoctorForm(doctor) {
  const errors = {};
  
  console.log('=== DEBUG VALIDAÇÃO MÉDICO ===');
  console.log('Dados recebidos:', doctor);
  
  // Validações obrigatórias
  if (!validateRequired(doctor.name)) {
    console.log('❌ Nome inválido:', doctor.name);
    errors.name = getErrorMessage('Nome', 'required');
  } else {
    console.log('✅ Nome válido:', doctor.name);
  }
  
  if (!validateEmail(doctor.email)) {
    console.log('❌ Email inválido:', doctor.email);
    errors.email = getErrorMessage('E-mail', 'invalidEmail');
  } else {
    console.log('✅ Email válido:', doctor.email);
  }
  
  if (!validateCNS(doctor.cns)) {
    console.log('❌ CNS inválido:', doctor.cns);
    errors.cns = getErrorMessage('CNS', 'invalidCNS');
  } else {
    console.log('✅ CNS válido:', doctor.cns);
  }
  
  if (!validateCRM(doctor.crm)) {
    console.log('❌ CRM inválido:', doctor.crm);
    errors.crm = getErrorMessage('CRM', 'invalidCRM');
  } else {
    console.log('✅ CRM válido:', doctor.crm);
  }
  
  if (!validateRequired(doctor.crm_state)) {
    console.log('❌ Estado CRM inválido:', doctor.crm_state);
    errors.crm_state = getErrorMessage('Estado do CRM', 'required');
  } else {
    console.log('✅ Estado CRM válido:', doctor.crm_state);
  }
  
  if (!validateRequired(doctor.specialty)) {
    console.log('❌ Especialidade inválida:', doctor.specialty);
    errors.specialty = getErrorMessage('Especialidade', 'required');
  } else {
    console.log('✅ Especialidade válida:', doctor.specialty);
  }
  
  // Validações opcionais
  if (doctor.phone && doctor.phone.trim() !== '' && !validatePhone(doctor.phone)) {
    console.log('❌ Telefone inválido:', doctor.phone);
    errors.phone = getErrorMessage('Telefone', 'invalidPhone');
  } else if (doctor.phone && doctor.phone.trim() !== '') {
    console.log('✅ Telefone válido:', doctor.phone);
  }
  
  if (doctor.cpf && doctor.cpf.trim() !== '' && !validateCPF(doctor.cpf)) {
    console.log('❌ CPF inválido:', doctor.cpf);
    errors.cpf = getErrorMessage('CPF', 'invalidCPF');
  } else if (doctor.cpf && doctor.cpf.trim() !== '') {
    console.log('✅ CPF válido:', doctor.cpf);
  }
  
  if (doctor.birth_date && doctor.birth_date.trim() !== '' && !validateBirthDate(doctor.birth_date)) {
    console.log('❌ Data nascimento inválida:', doctor.birth_date);
    errors.birth_date = getErrorMessage('Data de Nascimento', 'invalidBirthDate');
  } else if (doctor.birth_date && doctor.birth_date.trim() !== '') {
    console.log('✅ Data nascimento válida:', doctor.birth_date);
  }
  
  if (doctor.birth_date && doctor.birth_date.trim() !== '' && !validateMinAge(doctor.birth_date, 18)) {
    console.log('❌ Idade mínima não atingida:', doctor.birth_date);
    errors.birth_date = getErrorMessage('Data de Nascimento', 'invalidMinAge');
  }
  
  // Validações de endereço (se fornecido)
  if (doctor.address) {
    console.log('📍 Endereço fornecido:', doctor.address);
    
    // Só valida endereço se pelo menos um campo estiver preenchido
    const hasAddressFields = doctor.address.cep || doctor.address.street || doctor.address.number || 
                            doctor.address.neighborhood || doctor.address.city || doctor.address.state;
    
    if (hasAddressFields) {
      if (doctor.address.cep && doctor.address.cep.trim() !== '' && !validateCEP(doctor.address.cep)) {
        console.log('❌ CEP inválido:', doctor.address.cep);
        errors['address.cep'] = getErrorMessage('CEP', 'invalidCEP');
      } else if (doctor.address.cep && doctor.address.cep.trim() !== '') {
        console.log('✅ CEP válido:', doctor.address.cep);
      }
      
      if (doctor.address.state && doctor.address.state.trim() !== '' && !validateRequired(doctor.address.state)) {
        console.log('❌ Estado inválido:', doctor.address.state);
        errors['address.state'] = getErrorMessage('Estado', 'required');
      } else if (doctor.address.state && doctor.address.state.trim() !== '') {
        console.log('✅ Estado válido:', doctor.address.state);
      }
      
      if (doctor.address.city && doctor.address.city.trim() !== '' && !validateRequired(doctor.address.city)) {
        console.log('❌ Cidade inválida:', doctor.address.city);
        errors['address.city'] = getErrorMessage('Cidade', 'required');
      } else if (doctor.address.city && doctor.address.city.trim() !== '') {
        console.log('✅ Cidade válida:', doctor.address.city);
      }
    } else {
      console.log('📍 Nenhum campo de endereço preenchido - endereço é opcional');
    }
  } else {
    console.log('📍 Nenhum endereço fornecido');
  }
  
  console.log('🔍 Erros encontrados:', errors);
  console.log('=== FIM DEBUG ===');
  
  return errors;
}